import React, { lazy, Suspense, useMemo, useCallback } from 'react';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { Provider } from 'react-redux';
import { useSelector, useDispatch } from 'react-redux';
import { store } from './store/store';
import { setCurrentPage } from './store/slices/uiSlice';
import { CustomThemeProvider } from './context/ThemeContext';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';

// Lazy load all components
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const Products = lazy(() => import('./components/products/Products'));
const Categories = lazy(() => import('./components/categories/Categories'));
const Inventory = lazy(() => import('./components/inventory/Inventory'));
const Orders = lazy(() => import('./components/orders/Orders'));
const Customers = lazy(() => import('./components/customers/Customers'));
const Reports = lazy(() => import('./components/reports/Reports'));
const Profile = lazy(() => import('./components/profile/Profile'));
const Settings = lazy(() => import('./components/settings/Settings'));

// Loading component
const LoadingSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <CircularProgress />
  </Box>
);

const AppContent = () => {
  const dispatch = useDispatch();
  const currentPage = useSelector((state) => state.ui.currentPage);

  const renderPage = useMemo(() => {
    const components = {
      dashboard: Dashboard,
      products: Products,
      categories: Categories,
      inventory: Inventory,
      orders: Orders,
      customers: Customers,
      reports: Reports,
      profile: Profile,
      settings: Settings,
    };
    
    const Component = components[currentPage] || Dashboard;
    return <Component />;
  }, [currentPage]);

  const handlePageChange = useCallback((page) => {
    dispatch(setCurrentPage(page));
  }, [dispatch]);

  return (
    <CustomThemeProvider>
      <CssBaseline />
      <Layout currentPage={currentPage} setCurrentPage={handlePageChange}>
        <Suspense fallback={<LoadingSpinner />}>
          {renderPage}
        </Suspense>
      </Layout>
    </CustomThemeProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <AppContent />
      </Provider>
    </ErrorBoundary>
  );
}

export default React.memo(App);